import { REPORT_CONFIGURATION_NAME } from "../types/constants";
import { buildConfigurationForCopy } from "../utils/configurationUtils";
import {
  validateReportName,
  isReportNameInUse,
  extractApiErrorMessage,
} from "../utils/reportDialogUtils";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreateReportMutation,
  useStoreReportConfigurationMutation,
} from "services/reportsV3Service";

export const useImportReport = (
  savedReports,
  failureMessage = "Failed to import report. Please try again."
) => {
  const navigate = useNavigate();
  const [createReport, { isLoading: isImporting }] = useCreateReportMutation();
  const [storeConfiguration] = useStoreReportConfigurationMutation();

  const fileInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importName, setImportName] = useState("");
  const [importDescription, setImportDescription] = useState("");
  const [importError, setImportError] = useState("");
  const [pendingData, setPendingData] = useState(null);

  const handleImportClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }, []);

  const handleFileSelected = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const nameFromFile = file.name
      .replace(/\.json$/i, "")
      .replace(/-edited$/i, "");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        setPendingData(parsed);
        setImportName(nameFromFile);
        setImportDescription(parsed.report?.description ?? "");
        setImportError("");
      } catch {
        setPendingData(null);
        setImportName(nameFromFile);
        setImportDescription("");
        setImportError("The selected file is not a valid JSON report.");
      }
      setDialogOpen(true);
    };
    reader.readAsText(file);
  }, []);

  // Opens the naming dialog for report data that did not come from a file,
  // such as a copy of a saved report; an error is shown in place of the data.
  const openWithData = useCallback((data, name, error = "") => {
    setPendingData(data);
    setImportName(name);
    setImportDescription(data?.report?.description ?? "");
    setImportError(error);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setTimeout(() => {
      setImportName("");
      setImportDescription("");
      setImportError("");
      setPendingData(null);
    }, 200);
  }, []);

  const clearImportError = useCallback(() => setImportError(""), []);

  const isNameUnique = useMemo(
    () => !isReportNameInUse(importName.trim(), savedReports, null),
    [importName, savedReports]
  );

  const handleImportConfirm = useCallback(async () => {
    if (!pendingData) return;

    const { valid, trimmed, error: nameError } = validateReportName(importName);
    if (!valid) {
      setImportError(nameError);
      return;
    }
    if (isReportNameInUse(trimmed, savedReports, null)) {
      setImportError(
        "A report with this name already exists. Choose another name."
      );
      return;
    }

    const { report = {}, configuration, sourceReportId } = pendingData;
    const reportData = {
      ...report,
      displayName: trimmed,
    };

    try {
      const result = await createReport(reportData).unwrap();
      const reportId = result.id;
      const newConfiguration = buildConfigurationForCopy(
        report,
        configuration,
        sourceReportId ?? report.internalName,
        reportId
      );
      if (
        reportId &&
        newConfiguration &&
        Object.keys(newConfiguration).length > 0
      ) {
        await storeConfiguration({
          reportId,
          configurationName: REPORT_CONFIGURATION_NAME,
          configuration: newConfiguration,
        }).unwrap();
      }
      handleDialogClose();
      if (reportId) {
        navigate(`/report-builder/${reportId}`);
      }
    } catch (error) {
      setImportError(extractApiErrorMessage(error, failureMessage));
    }
  }, [
    pendingData,
    importName,
    savedReports,
    createReport,
    storeConfiguration,
    navigate,
    handleDialogClose,
    failureMessage,
  ]);

  return {
    fileInputRef,
    dialogOpen,
    importName,
    setImportName,
    importDescription,
    setImportDescription,
    importError,
    clearImportError,
    isNameUnique,
    isImporting,
    handleImportClick,
    handleFileSelected,
    openWithData,
    handleDialogClose,
    handleImportConfirm,
  };
};
